import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { RedImmobile, RedBadgeAccesso } from '../../../types/red'
import { redApi } from '../../../services/api'
import { QrCode, Download } from 'lucide-react'

function QrBadgeModal({ accesso, onClose }: { accesso: RedBadgeAccesso; onClose: () => void }) {
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  useEffect(() => {
    redApi.badgeAccessi.qr(accesso.id).then(res => {
      const url = window.URL.createObjectURL(res.data)
      setQrUrl(url)
      return () => window.URL.revokeObjectURL(url)
    })
  }, [accesso.id])
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold mb-1">Badge Digitale</h3>
        <p className="text-sm text-gray-500 mb-4">{accesso.badge_id} — {accesso.nome_lavoratore} {accesso.cognome_lavoratore}</p>
        {qrUrl ? (
          <>
            <img src={qrUrl} alt="QR Badge" className="mx-auto mb-4 rounded-lg border" />
            <a
              href={qrUrl}
              download={`badge_${accesso.badge_id}.png`}
              className="inline-flex items-center gap-1.5 text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700"
            >
              <Download size={14} /> Scarica PNG
            </a>
          </>
        ) : (
          <div className="py-8 text-gray-400">Generazione QR...</div>
        )}
        <button onClick={onClose} className="block mx-auto mt-4 text-sm text-gray-500 hover:text-gray-700">Chiudi</button>
      </div>
    </div>
  )
}

const TabBadgeCantiere: React.FC<{ immobile: RedImmobile; onUpdate: () => void }> = ({ immobile, onUpdate }) => {
  const [accessi, setAccessi] = useState<RedBadgeAccesso[]>([])
  const [report, setReport] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [qrAccesso, setQrAccesso] = useState<RedBadgeAccesso | null>(null)
  const [form, setForm] = useState({
    badge_id: '',
    nome_lavoratore: '',
    cognome_lavoratore: '',
    cf_lavoratore: '',
    ditta: '',
    mansione: '',
    data_ingresso: new Date().toISOString().slice(0, 16),
    note: '',
  })

  const load = () => {
    redApi.badgeAccessi.listByImmobile(immobile.id).then(res => {
      setAccessi(res.data)
      setLoading(false)
    })
    redApi.badgeAccessi.report(immobile.id).then(res => {
      setReport(res.data)
    })
  }

  useEffect(load, [immobile.id])

  const handleCreate = async () => {
    if (!form.badge_id || !form.nome_lavoratore || !form.cognome_lavoratore) {
      toast.error('Badge ID, nome e cognome sono obbligatori')
      return
    }
    try {
      await redApi.badgeAccessi.create(immobile.id, {
        badge_id: form.badge_id,
        nome_lavoratore: form.nome_lavoratore,
        cognome_lavoratore: form.cognome_lavoratore,
        cf_lavoratore: form.cf_lavoratore || null,
        ditta: form.ditta || null,
        mansione: form.mansione || null,
        data_ingresso: new Date(form.data_ingresso).toISOString(),
        note: form.note || null,
      })
      toast.success('Accesso registrato')
      setForm({
        badge_id: '',
        nome_lavoratore: '',
        cognome_lavoratore: '',
        cf_lavoratore: '',
        ditta: '',
        mansione: '',
        data_ingresso: new Date().toISOString().slice(0, 16),
        note: '',
      })
      setShowForm(false)
      load()
      onUpdate()
    } catch {
      toast.error('Errore registrazione accesso')
    }
  }

  const handleUscita = async (id: string) => {
    try {
      await redApi.badgeAccessi.update(id, { data_uscita: new Date().toISOString() })
      toast.success('Uscita registrata')
      load()
      onUpdate()
    } catch {
      toast.error('Errore registrazione uscita')
    }
  }

  if (loading) return <div className="text-gray-400">Caricamento badge...</div>

  return (
    <div>
      {/* Report */}
      {report && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">Totale Accessi</div>
            <div className="text-xl font-bold text-blue-700">{report.totale_accessi}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">Lavoratori Distinti</div>
            <div className="text-xl font-bold text-green-700">{report.lavoratori_distinti}</div>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="text-sm text-gray-500">In Cantiere Ora</div>
            <div className="text-xl font-bold text-orange-700">{report.accessi_aperti}</div>
          </div>
        </div>
      )}

      {/* Lavoratori in cantiere */}
      {report?.lavoratori_in_cantiere?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <h3 className="text-sm font-semibold text-amber-800 mb-2">🚧 Lavoratori in cantiere</h3>
          <div className="flex flex-wrap gap-2">
            {report.lavoratori_in_cantiere.map((l: any) => (
              <span key={l.id} className="text-xs bg-white border border-amber-200 rounded-lg px-2 py-1">
                {l.badge_id} — {l.nome} {l.cognome}
                {l.ditta && ` (${l.ditta})`}
                {l.mansione && ` — ${l.mansione}`}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Form nuovo accesso */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Registro Accessi</h2>
        <button onClick={() => setShowForm(v => !v)} className="btn-primary text-sm">
          {showForm ? 'Annulla' : '+ Registra accesso'}
        </button>
      </div>

      {showForm && (
        <div className="card p-4 mb-6 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div>
            <label className="label">Badge ID *</label>
            <input className="input" value={form.badge_id} onChange={e => setForm({ ...form, badge_id: e.target.value })} placeholder="Es. BD-001" />
          </div>
          <div>
            <label className="label">Nome *</label>
            <input className="input" value={form.nome_lavoratore} onChange={e => setForm({ ...form, nome_lavoratore: e.target.value })} />
          </div>
          <div>
            <label className="label">Cognome *</label>
            <input className="input" value={form.cognome_lavoratore} onChange={e => setForm({ ...form, cognome_lavoratore: e.target.value })} />
          </div>
          <div>
            <label className="label">Codice Fiscale</label>
            <input className="input" value={form.cf_lavoratore} onChange={e => setForm({ ...form, cf_lavoratore: e.target.value })} maxLength={16} />
          </div>
          <div>
            <label className="label">Ditta</label>
            <input className="input" value={form.ditta} onChange={e => setForm({ ...form, ditta: e.target.value })} />
          </div>
          <div>
            <label className="label">Mansione</label>
            <input className="input" value={form.mansione} onChange={e => setForm({ ...form, mansione: e.target.value })} placeholder="Es. Muratore, Carpentiere" />
          </div>
          <div>
            <label className="label">Data/Ora ingresso *</label>
            <input type="datetime-local" className="input" value={form.data_ingresso} onChange={e => setForm({ ...form, data_ingresso: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="label">Note</label>
            <input className="input" value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          </div>
          <div className="col-span-3">
            <button onClick={handleCreate} className="btn-primary">Registra ingresso</button>
          </div>
        </div>
      )}

      {/* Lista accessi */}
      <div className="space-y-2">
        {accessi.map(acc => {
          const inCantiere = !acc.data_uscita
          return (
            <div key={acc.id} className={`flex items-center justify-between bg-white border rounded-lg p-3 ${inCantiere ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
              <div>
                <div className="text-sm font-medium text-gray-800">
                  {acc.badge_id} — {acc.nome_lavoratore} {acc.cognome_lavoratore}
                  {inCantiere && <span className="ml-2 text-xs bg-green-600 text-white px-1.5 py-0.5 rounded-full">IN CANTIERE</span>}
                </div>
                <div className="text-xs text-gray-500">
                  {acc.ditta && `Ditta: ${acc.ditta} · `}
                  {acc.mansione && `Mansione: ${acc.mansione} · `}
                  Ingresso: {new Date(acc.data_ingresso).toLocaleString('it-IT')}
                  {acc.data_uscita && ` · Uscita: ${new Date(acc.data_uscita).toLocaleString('it-IT')}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQrAccesso(acc)}
                  className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded hover:bg-blue-100 flex items-center gap-1"
                  title="QR Badge"
                >
                  <QrCode size={12} /> QR
                </button>
                {inCantiere && (
                  <button onClick={() => handleUscita(acc.id)} className="text-xs bg-amber-500 text-white px-2 py-1 rounded hover:bg-amber-600">
                    Registra uscita
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (!confirm('Eliminare questo accesso?')) return
                    try { await redApi.badgeAccessi.delete(acc.id); toast.success('Accesso eliminato'); load(); onUpdate() } catch { toast.error('Errore') }
                  }}
                  className="text-xs text-red-500 hover:text-red-700"
                  title="Elimina"
                >
                  🗑
                </button>
              </div>
            </div>
          )
        })}
        {accessi.length === 0 && (
          <div className="text-center py-10 text-gray-400">Nessun accesso registrato.</div>
        )}
      </div>
      {qrAccesso && <QrBadgeModal accesso={qrAccesso} onClose={() => setQrAccesso(null)} />}
    </div>
  )
}

export default TabBadgeCantiere
